/** Video URL helpers — YouTube + Vimeo embedding */

export function isYouTube(url) {
  if (!url) return false;
  return /(?:youtube\.com|youtu\.be)/.test(url);
}

export function toYouTubeEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}?rel=0&modestbranding=1`;
    }
    if (u.pathname.startsWith("/embed/")) return url;
    const id = u.searchParams.get("v");
    if (id) return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
  } catch {
    /* ignore parse errors */
  }
  return url;
}

/** Detects raw iframe / HTML tags that admin should NOT paste here. */
export function looksLikeHtml(input) {
  if (!input) return false;
  return /<\s*iframe\b|<\s*[a-z]/i.test(String(input));
}

/** Returns true for any vimeo.com or player.vimeo.com URL. */
export function isVimeo(url) {
  if (!url) return false;
  if (looksLikeHtml(url)) return false;
  return /(?:^|\/\/|\.)(?:vimeo\.com|player\.vimeo\.com)/.test(url);
}

/**
 * Extract numeric video id from any supported Vimeo URL form, e.g.
 *   https://vimeo.com/123456789
 *   https://vimeo.com/123456789?share=copy
 *   https://vimeo.com/123456789/abcd1234   (private/showcase hash)
 *   https://player.vimeo.com/video/123456789
 *   vimeo.com/123456789
 */
export function extractVimeoId(url) {
  if (!url) return null;
  if (looksLikeHtml(url)) return null;
  const m = String(url).match(
    /vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)/i,
  );
  return m ? m[1] : null;
}

/**
 * Convert any Vimeo URL form to its canonical embed URL.
 * Returns null if URL isn't a recognisable Vimeo link.
 * Preserves the `h` token (unlisted-video) whether it appears as a path
 * segment (`vimeo.com/123/abc12345`) or query string (`?h=abc12345`).
 */
export function toVimeoEmbed(url) {
  const id = extractVimeoId(url);
  if (!id) return null;
  // 1) Path form: vimeo.com/123/<hash>
  const pathHash = String(url).match(new RegExp(`vimeo\\.com\\/(?:video\\/)?${id}\\/([a-f0-9]+)`, "i"));
  if (pathHash) return `https://player.vimeo.com/video/${id}?h=${pathHash[1]}`;
  // 2) Query-string form: ?h=<hash> (unlisted player URLs)
  const qsHash = String(url).match(/[?&]h=([a-f0-9]+)/i);
  if (qsHash) return `https://player.vimeo.com/video/${id}?h=${qsHash[1]}`;
  return `https://player.vimeo.com/video/${id}`;
}

/** True if the url is already a player.vimeo.com embed URL. */
export function isVimeoEmbed(url) {
  return /^https?:\/\/player\.vimeo\.com\/video\/\d+/i.test(url || "");
}

/**
 * Normalise any video URL on save:
 *   - Vimeo regular URL → player embed
 *   - YouTube watch URL → keep watch URL (frontend has its own embed converter)
 *   - Internal /api/files/... → keep
 *   - Anything else → unchanged
 */
export function normalizeVideoUrl(url) {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (isVimeo(trimmed)) {
    const embed = toVimeoEmbed(trimmed);
    return embed || trimmed;
  }
  return trimmed;
}
